import React, { useState, useEffect } from 'react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import OTPLoginModal from './OTPLoginModal';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import {
  X, Loader2, CheckCircle, Users, IndianRupee,
  Calendar, MapPin, Clock, ShieldCheck, Minus, Plus, Tag, AlertCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY = process.env.REACT_APP_COMMUNITY_GAMES_RAZORPAY_KEY_ID || process.env.REACT_APP_RAZORPAY_KEY_ID;

const POSITIONS = ['', 'GK', 'Defender', 'Midfielder', 'Attacker'];

const normPhone = (s) => (s || '').replace(/\D/g, '').slice(0, 10);

/**
 * Registration modal — handles all 4 formats.
 *
 * Props:
 *   event       — public event detail
 *   activity    — activity the user is registering for
 *   onClose()   — caller closes the modal (no reload)
 *   onRegistered(registration) — caller reloads the event after success
 */
const EventRegisterModal = ({ event, activity, onClose, onRegistered }) => {
  const { user, isAuthenticated } = useCustomerAuth();
  const [phase, setPhase] = useState('form'); // form | otp | submitting | success
  const [submitting, setSubmitting] = useState(false);
  const [registration, setRegistration] = useState(null);

  // Form state — preserved across OTP login
  const isTeam = activity.format === 'team';
  const isFree = activity.format === 'free' || (activity.price_per_unit || 0) === 0;
  const spotsLeft = Math.max(0, (activity.max_participants || 0) - (activity.current_registrations || 0));
  const maxQty = Math.min(10, Math.max(1, spotsLeft));
  const [quantity, setQuantity] = useState(1);
  const [name, setName] = useState(user?.name || '');
  // Coupon state
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null); // {coupon_id, coupon_code, discount_amount, final_amount, original_amount, discount_per_ticket, message}
  const [couponError, setCouponError] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  // Re-validate when quantity changes (discount scales per ticket)
  useEffect(() => {
    if (coupon && !validatingCoupon) {
      revalidateCoupon(coupon.coupon_code);
    }
  }, [quantity]); // eslint-disable-line
  const [phone, setPhone] = useState(normPhone(user?.phone));
  const [email, setEmail] = useState(user?.email || '');
  const [teamName, setTeamName] = useState('');
  const [players, setPlayers] = useState(
    isTeam ? Array.from({ length: activity.team_size || 1 }, () => ({ name: '', phone: '', position: '' })) : []
  );

  // Hydrate captain (player 1) from logged-in user
  useEffect(() => {
    if (isTeam && user && (!players[0]?.name || !players[0]?.phone)) {
      setPlayers(prev => prev.map((p, i) => i === 0 ? { name: user.name || p.name, phone: normPhone(user.phone) || p.phone, position: p.position } : p));
    }
    if (!isTeam && user) {
      if (!name) setName(user.name || '');
      if (!phone) setPhone(normPhone(user.phone));
    }
  }, [user, isTeam]); // intentionally omit name/phone/players to avoid loops

  const setPlayer = (i, patch) => setPlayers(arr => arr.map((p, idx) => idx === i ? { ...p, ...patch } : p));

  // ---- Coupon helpers ----
  const revalidateCoupon = async (code) => {
    if (!code) return;
    const norm = normPhone(phone);
    if (norm.length !== 10) return;
    try {
      const res = await fetch(`${API_URL}/api/events/public/validate-coupon`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coupon_code: code,
          event_id: event.id,
          activity_id: activity.id,
          customer_phone: norm,
          quantity,
          original_amount: (activity.price_per_unit || 0) * quantity,
        }),
      });
      const data = await res.json();
      if (data.valid) {
        setCoupon(data);
        setCouponError('');
      } else {
        setCoupon(null);
        setCouponError(data.message || 'Coupon no longer valid');
      }
    } catch (_err) {
      // silent — leave existing state
    }
  };

  const applyCoupon = async () => {
    setCouponError('');
    const code = (couponInput || '').trim().toUpperCase();
    if (!code) return;
    const norm = normPhone(phone);
    if (norm.length !== 10) {
      setCouponError('Enter a valid 10-digit phone first');
      return;
    }
    setValidatingCoupon(true);
    try {
      const res = await fetch(`${API_URL}/api/events/public/validate-coupon`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          coupon_code: code,
          event_id: event.id,
          activity_id: activity.id,
          customer_phone: norm,
          quantity,
          original_amount: (activity.price_per_unit || 0) * quantity,
        }),
      });
      const data = await res.json();
      if (data.valid) {
        setCoupon(data);
        setCouponInput(code);
        setCouponError('');
      } else {
        setCoupon(null);
        setCouponError(data.message || 'Invalid coupon code');
      }
    } catch (_err) {
      setCouponError('Could not validate coupon. Try again.');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponInput('');
    setCouponError('');
  };

  // Two-player team activities (padel, badminton/pickleball doubles) get a
  // simplified UI: no team-name input (auto-derived from player names),
  // no position dropdown. Everything else in the team flow stays untouched.
  const isTwoPlayerTeam = isTeam && (activity.team_size === 2);

  // Validation
  const formValid = (() => {
    if (isTeam) {
      const required = activity.team_size || 0;
      if (players.length !== required) return false;
      for (const p of players) {
        if (!p.name?.trim() || normPhone(p.phone).length !== 10) return false;
      }
      // Team name required for regular team flows; auto-derived for 2-player teams.
      if (!isTwoPlayerTeam && !teamName.trim()) return false;
      return true;
    }
    return name.trim().length > 0 && normPhone(phone).length === 10;
  })();

  const handleStart = () => {
    // Submit button is disabled when !formValid, so guard is informational only.
    if (!formValid) return;
    if (!isAuthenticated) {
      setPhase('otp');
      return;
    }
    submit();
  };

  const handleLoginSuccess = () => {
    setPhase('form');
    // Form state is intact (component never unmounted). Auto-submit.
    setTimeout(() => submit(), 200);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = isTeam
        ? { team_name: (isTwoPlayerTeam
              ? players.map(p => p.name.trim()).join(' / ')
              : teamName.trim()),
            email: email.trim() || undefined,
            players: players.map(p => ({
              name: p.name.trim(),
              phone: normPhone(p.phone),
              position: (isTwoPlayerTeam ? undefined : (p.position || undefined)),
            })) }
        : { name: name.trim(), phone: normPhone(phone), email: email.trim() || undefined,
            quantity, coupon_code: coupon?.coupon_code || undefined };

      const res = await fetch(
        `${API_URL}/api/events/public/${event.id}/activities/${activity.id}/register`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Registration failed');
      }
      const data = await res.json();

      if (data.is_free) {
        setRegistration({
          ...data.registration,
          quantity: data.quantity || 1,
          registration_ids: data.registration_ids || [data.registration?.id],
          coupon_invalidated: data.coupon_invalidated || null,
          coupon_code: data.registration?.coupon_code || coupon?.coupon_code,
          original_amount: data.original_amount,
          final_amount: data.final_amount,
          discount_amount: data.discount_amount,
        });
        setPhase('success');
        return;
      }

      // If user passed a coupon but backend says it was no longer valid, surface a toast (and we'll
      // also show the note on the success screen).
      if (data.coupon_invalidated) {
        toast({
          title: 'Coupon not applied',
          description: `${data.coupon_invalidated}. Full price was charged.`,
          variant: 'destructive',
        });
      }

      // Paid path — open Razorpay
      const options = {
        key: data.key_id || RAZORPAY_KEY,
        amount: data.amount,
        currency: data.currency,
        name: event.venue_name,
        description: `${event.title} — ${activity.title}`,
        order_id: data.order_id,
        prefill: {
          contact: `+91${isTeam ? normPhone(players[0]?.phone) : normPhone(phone)}`,
          name: isTeam ? (players[0]?.name || '') : name,
          email: email || undefined,
        },
        theme: { color: '#0A2C93' },
        handler: async (response) => {
          try {
            const v = await fetch(
              `${API_URL}/api/events/public/${event.id}/activities/${activity.id}/verify-payment`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                }),
              },
            );
            if (!v.ok) {
              const err = await v.json().catch(() => ({}));
              throw new Error(err.detail || 'Payment verification failed');
            }
            const final = await v.json();
            const qty = final.quantity || 1;
            setRegistration({
              id: final.registration_id,
              registration_ids: final.registration_ids || [final.registration_id],
              quantity: qty,
              amount_paid: final.amount_paid != null ? final.amount_paid : ((activity.price_per_unit || 0) * qty),
              registration_type: isTeam ? 'team' : (activity.format === 'ticket' ? 'ticket' : 'individual'),
              team_name: teamName, players,
              name, phone: normPhone(phone), email,
              coupon_invalidated: data.coupon_invalidated || null,
              coupon_code: data.coupon_invalidated ? null : (coupon?.coupon_code || null),
              original_amount: data.original_amount,
              final_amount: data.final_amount,
              discount_amount: data.coupon_invalidated ? 0 : (data.discount_amount || 0),
            });
            setPhase('success');
          } catch (err) {
            toast({ title: 'Payment failed', description: err.message, variant: 'destructive' });
            setPhase('form');
          }
        },
        modal: {
          ondismiss: () => {
            toast({ title: 'Payment cancelled', description: 'You can try again anytime.', variant: 'destructive' });
            setPhase('form');
            setSubmitting(false);
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // ------- Success screen -------
  if (phase === 'success' && registration) {
    return (
      <ModalShell onClose={onClose} testId="register-success-modal">
        <SuccessScreen
          event={event}
          activity={activity}
          registration={registration}
          onDone={() => { onRegistered(registration); onClose(); }}
        />
      </ModalShell>
    );
  }

  return (
    <>
      <ModalShell onClose={onClose} testId="register-modal">
        <div className="px-5 pt-4 pb-5 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-bold text-base">Register</h2>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="register-close">
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-3 line-clamp-2">{event.title} · {activity.title}</p>

          {/* Activity summary */}
          <div className="bg-gray-50 rounded-xl p-3 mb-4 text-xs text-gray-700 space-y-1">
            <p className="flex items-center gap-1.5"><Calendar className="h-3 w-3" />{activity.date} · {activity.start_time}–{activity.end_time}</p>
            <p className="flex items-center gap-1.5"><MapPin className="h-3 w-3" />{event.venue_name}</p>
            <p className="flex items-center gap-1.5">
              {isFree ? <span className="font-semibold text-emerald-600">Free</span>
                      : <><IndianRupee className="h-3 w-3" /><span className="font-semibold">{activity.price_per_unit}{isTeam ? ' / team' : ''}</span></>}
            </p>
          </div>

          {/* Form */}
          {isTeam ? (
            <div className="space-y-3">
              {!isTwoPlayerTeam && (
                <Field label="Team name *">
                  <input value={teamName} onChange={e => setTeamName(e.target.value)}
                    placeholder="e.g. Eagles FC"
                    className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="reg-team-name" />
                </Field>
              )}
              <Field label="Captain email (optional — for confirmation)">
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="reg-team-email" />
              </Field>
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-2">
                  <Users className="h-3.5 w-3.5" />Players ({players.length} of {activity.team_size})
                </p>
                <div className="space-y-2">
                  {players.map((p, i) => (
                    <div key={i}
                      className={isTwoPlayerTeam
                        ? "grid grid-cols-12 gap-1.5 items-center"
                        : "grid grid-cols-12 gap-1.5 items-center"}
                      data-testid={`reg-player-${i}`}>
                      <div className="col-span-2 text-[11px] font-semibold text-gray-500">
                        {isTwoPlayerTeam ? `Player ${i + 1}` : (i + 1)}
                      </div>
                      <input
                        className={isTwoPlayerTeam
                          ? "col-span-5 border rounded-lg px-2 py-1.5 text-sm"
                          : "col-span-4 border rounded-lg px-2 py-1.5 text-sm"}
                        placeholder={isTwoPlayerTeam
                          ? `Name ${i + 1}`
                          : (i === 0 ? 'Captain name' : 'Player name')}
                        value={p.name}
                        onChange={e => setPlayer(i, { name: e.target.value })}
                        data-testid={`reg-player-${i}-name`}
                      />
                      <input
                        type="tel"
                        className={isTwoPlayerTeam
                          ? "col-span-5 border rounded-lg px-2 py-1.5 text-sm"
                          : "col-span-4 border rounded-lg px-2 py-1.5 text-sm"}
                        placeholder="Phone"
                        value={p.phone}
                        onChange={e => setPlayer(i, { phone: normPhone(e.target.value) })}
                        data-testid={`reg-player-${i}-phone`}
                      />
                      {!isTwoPlayerTeam && (
                        <select
                          value={p.position}
                          onChange={e => setPlayer(i, { position: e.target.value })}
                          className="col-span-2 border rounded-lg px-1 py-1.5 text-[11px] bg-white"
                          data-testid={`reg-player-${i}-position`}
                        >
                          {POSITIONS.map(pos => <option key={pos} value={pos}>{pos || '—'}</option>)}
                        </select>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 mt-2">
                  {isTwoPlayerTeam
                    ? `All ${activity.team_size} player names + phone numbers are required.`
                    : `All ${activity.team_size} player names + phone numbers are required. Position is optional.`}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <Field label="Full name *">
                <input value={name} onChange={e => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="reg-name" />
              </Field>
              <Field label="Phone *">
                <input type="tel" value={phone} onChange={e => setPhone(normPhone(e.target.value))}
                  placeholder="10-digit mobile"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="reg-phone" />
              </Field>
              <Field label="Email (optional — for confirmation)">
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="reg-email" />
              </Field>

              {/* Coupon — paid activities only */}
              {!isFree && (
                <div data-testid="coupon-section">
                  {!coupon ? (
                    <>
                      <span className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                        <Tag className="h-3.5 w-3.5" /> Have a coupon code?
                      </span>
                      <div className="mt-1 flex items-center gap-2">
                        <input
                          type="text"
                          value={couponInput}
                          onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                          placeholder="Enter coupon code (optional)"
                          className="flex-1 border rounded-lg px-3 py-2 text-sm uppercase"
                          data-testid="reg-coupon-input"
                          disabled={validatingCoupon}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={applyCoupon}
                          disabled={validatingCoupon || !couponInput.trim()}
                          className="h-10"
                          data-testid="reg-coupon-apply"
                        >
                          {validatingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                        </Button>
                      </div>
                      {couponError && (
                        <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1" data-testid="reg-coupon-error">
                          <AlertCircle className="h-3 w-3" />{couponError}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5" data-testid="reg-coupon-applied">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5">
                          <Tag className="h-3.5 w-3.5" />
                          {coupon.coupon_code} applied — ₹{(coupon.discount_amount || 0).toLocaleString('en-IN')} off
                        </p>
                        <button type="button" onClick={removeCoupon}
                          className="text-xs font-semibold text-red-600 hover:underline"
                          data-testid="reg-coupon-remove">Remove</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Quantity selector — individual & ticket only */}
              <div>
                <span className="text-xs font-semibold text-gray-700">Number of tickets</span>
                <div className="mt-1 flex items-center gap-3" data-testid="reg-quantity-row">
                  <div className="flex items-center border rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      disabled={quantity <= 1}
                      className="px-3 py-2 hover:bg-gray-50 disabled:opacity-30"
                      aria-label="Decrease quantity"
                      data-testid="reg-quantity-decrease"
                    ><Minus className="h-4 w-4" /></button>
                    <input
                      type="number"
                      min="1" max={maxQty}
                      value={quantity}
                      onChange={e => {
                        const n = Math.max(1, Math.min(maxQty, Number(e.target.value) || 1));
                        setQuantity(n);
                      }}
                      className="w-12 text-center text-sm py-2 outline-none"
                      data-testid="reg-quantity-input"
                    />
                    <button
                      type="button"
                      onClick={() => setQuantity(q => Math.min(maxQty, q + 1))}
                      disabled={quantity >= maxQty}
                      className="px-3 py-2 hover:bg-gray-50 disabled:opacity-30"
                      aria-label="Increase quantity"
                      data-testid="reg-quantity-increase"
                    ><Plus className="h-4 w-4" /></button>
                  </div>
                  {!isFree && (
                    <div className="text-xs text-gray-600 space-y-0.5" data-testid="reg-quantity-price">
                      <p>
                        {quantity} {quantity === 1 ? 'ticket' : 'tickets'} × ₹{activity.price_per_unit}
                        {!coupon && (
                          <span className="font-bold text-gray-900"> = ₹{(activity.price_per_unit * quantity).toLocaleString('en-IN')}</span>
                        )}
                      </p>
                      {coupon && (
                        <div className="bg-gray-50 rounded-lg p-2 mt-1 space-y-0.5" data-testid="reg-price-breakdown">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Original</span>
                            <span className="text-gray-900">₹{(coupon.original_amount || activity.price_per_unit * quantity).toLocaleString('en-IN')}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-emerald-700">Discount ({coupon.coupon_code})</span>
                            <span className="text-emerald-700 font-semibold">−₹{(coupon.discount_amount || 0).toLocaleString('en-IN')}</span>
                          </div>
                          <div className="flex justify-between border-t border-gray-200 pt-1 mt-1">
                            <span className="font-bold text-gray-900">Total</span>
                            <span className="font-bold text-gray-900">₹{(coupon.final_amount || 0).toLocaleString('en-IN')}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {spotsLeft < 10 && spotsLeft > 0 && (
                  <p className="text-[11px] text-amber-700 mt-1" data-testid="reg-quantity-limit-note">
                    Only {spotsLeft} {spotsLeft === 1 ? 'spot' : 'spots'} left
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Terms & Conditions + Refund Policy — collapsible, only when set on the event */}
          {(event.terms_and_conditions || event.refund_policy) && (
            <div className="mt-4 space-y-2" data-testid="reg-policies">
              {event.terms_and_conditions && (
                <details className="border rounded-lg text-xs" data-testid="reg-terms">
                  <summary className="cursor-pointer px-3 py-2 font-semibold text-gray-700 select-none">
                    Terms &amp; Conditions
                  </summary>
                  <div className="px-3 pb-3 text-gray-600 whitespace-pre-wrap">
                    {event.terms_and_conditions}
                  </div>
                </details>
              )}
              {event.refund_policy && (
                <details className="border rounded-lg text-xs" data-testid="reg-refund-policy">
                  <summary className="cursor-pointer px-3 py-2 font-semibold text-gray-700 select-none">
                    Cancellation &amp; Refund Policy
                  </summary>
                  <div className="px-3 pb-3 text-gray-600 whitespace-pre-wrap">
                    {event.refund_policy}
                  </div>
                </details>
              )}
            </div>
          )}

          {/* Footer / CTA */}
          <div className="mt-5 sticky bottom-0 -mx-5 -mb-5 px-5 py-3 bg-white border-t">
            {(() => {
              const baseTotal = (activity.price_per_unit || 0) * quantity;
              const total = coupon ? (coupon.final_amount || 0) : baseTotal;
              const isFreeAfterDiscount = !isFree && coupon && total <= 0;
              let label;
              if (isFree) {
                label = isTeam
                  ? 'Confirm Registration'
                  : `Confirm ${quantity > 1 ? `${quantity} Tickets` : 'Registration'}`;
              } else if (isTeam) {
                label = `Pay ₹${activity.price_per_unit} & Register Team`;
              } else if (isFreeAfterDiscount) {
                label = quantity > 1 ? `Register ${quantity} Tickets Free` : 'Register Free';
              } else {
                label = `Pay ₹${Number(total).toLocaleString('en-IN')} & Register${quantity > 1 ? ` ${quantity} Tickets` : ''}`;
              }
              return (
                <Button
                  onClick={handleStart}
                  disabled={!formValid || submitting}
                  className="w-full h-12 text-base font-semibold"
                  data-testid="reg-submit"
                >
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  {label}
                </Button>
              );
            })()}
            <p className="flex items-center justify-center gap-1 text-[10px] text-gray-400 mt-2">
              <ShieldCheck className="h-3 w-3" />Full refund on cancellation
            </p>
          </div>
        </div>
      </ModalShell>

      {/* OTP login — form state is preserved because this modal doesn't unmount */}
      {phase === 'otp' && (
        <OTPLoginModal
          onClose={() => setPhase('form')}
          onSuccess={handleLoginSuccess}
        />
      )}
    </>
  );
};

// ------- Subcomponents -------
const ModalShell = ({ children, onClose, testId }) => (
  <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" data-testid={testId}>
    <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-xl">
      {children}
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold text-gray-700">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const SuccessScreen = ({ event, activity, registration, onDone }) => {
  const isTeam = registration.registration_type === 'team';
  const amount = registration.amount_paid || 0;
  const quantity = registration.quantity || 1;
  const isMulti = !isTeam && quantity > 1;
  const regIds = registration.registration_ids || [registration.id];
  const couponInvalidated = registration.coupon_invalidated;
  const couponCode = registration.coupon_code;
  const original = registration.original_amount;
  const discount = registration.discount_amount;
  return (
    <div className="px-5 pt-5 pb-5 max-h-[90vh] overflow-y-auto text-center" data-testid="register-success">
      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
        <CheckCircle className="h-8 w-8 text-emerald-600" />
      </div>
      <h2 className="text-xl font-bold">You're registered!</h2>
      <p className="text-sm text-gray-500 mt-1">
        {isTeam
          ? <>Team <strong>{registration.team_name}</strong> is confirmed.</>
          : isMulti
            ? <>You have registered <strong>{quantity} tickets</strong> for {activity.title}.</>
            : 'See you on game day.'}
      </p>

      {couponInvalidated && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-left flex items-start gap-2" data-testid="coupon-invalidated-note">
          <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-900">
            Note: Your coupon was no longer valid at time of payment ({couponInvalidated}). Full price was charged.
          </p>
        </div>
      )}

      <div className="bg-gray-50 rounded-xl p-4 mt-4 text-left space-y-2 text-sm">
        <Row label="Event" value={event.title} />
        <Row label="Activity" value={activity.title} />
        <Row label="Date" value={activity.date} icon={Calendar} />
        <Row label="Time" value={`${activity.start_time} – ${activity.end_time}`} icon={Clock} />
        <Row label="Venue" value={event.venue_name} icon={MapPin} />
        {isMulti && <Row label="Tickets" value={`${quantity}`} />}
        {couponCode && !couponInvalidated && original != null && discount > 0 && (
          <>
            <Row label="Original" value={`₹${Number(original).toLocaleString('en-IN')}`} />
            <Row label={`Discount (${couponCode})`} value={`−₹${Number(discount).toLocaleString('en-IN')}`} />
          </>
        )}
        <Row label="Amount" value={amount > 0 ? `₹${Number(amount).toLocaleString('en-IN')}` : 'Free'} highlight />
      </div>

      {isMulti && regIds.length > 0 && (
        <div className="bg-white border rounded-xl p-3 mt-3 text-left" data-testid="success-reg-ids">
          <p className="text-[10px] uppercase text-gray-500 mb-1">Registration IDs</p>
          <ul className="space-y-1 text-xs font-mono break-all">
            {regIds.map((rid, i) => (
              <li key={rid} className="flex items-center justify-between gap-2">
                <span className="text-gray-500">Ticket {i + 1}</span>
                <span>{rid}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isTeam && (registration.players || []).length > 0 && (
        <div className="bg-white border rounded-xl p-3 mt-3 text-left">
          <p className="text-[10px] uppercase text-gray-500 mb-1">Team Roster</p>
          <ul className="space-y-1.5 text-xs">
            {registration.players.map((p, i) => (
              <li key={i} className="flex items-center justify-between gap-2">
                <span><strong>{i + 1}. {p.name}</strong></span>
                <span className="text-gray-500">{p.phone}{p.position ? ` · ${p.position}` : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Button onClick={onDone} className="w-full mt-5 h-12" data-testid="register-success-done">
        Done
      </Button>
    </div>
  );
};

const Row = ({ label, value, icon: Icon, highlight }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-xs text-gray-500 flex items-center gap-1">{Icon ? <Icon className="h-3 w-3" /> : null}{label}</span>
    <span className={`text-sm ${highlight ? 'font-bold text-emerald-600' : 'font-medium'}`}>{value}</span>
  </div>
);

export default EventRegisterModal;
